import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import { IEcgRecording, IEcgRecordingSummary } from "../../@types/Ecg";
import { ecgService } from "../../services/ecgService";
import { mapEcgSummary } from "../../utils/ecg/mapEcgRecording";
import { parseEcgData } from "../../utils/ecg/parseEcgData";

/**
 * Lists a patient's ECG recordings (metadata only — no samples), newest first.
 * Optional `fromDate`/`toDate` (YYYY-MM-DD) filter the range server-side; the
 * query refetches whenever they change.
 *
 * The waveform samples for each recording are fetched lazily, on demand, via
 * {@link useEcgWaveform} when its tracing is opened.
 */
export function useEcgRecordings(
  patientId: number,
  fromDate?: string,
  toDate?: string,
  enabled = true
) {
  const {
    data,
    error,
    isError,
    isFetching,
    isInitialLoading,
    refetch,
    isRefetching,
  } = useQuery<IEcgRecordingSummary[]>({
    queryKey: ["ecg-recordings", patientId, fromDate ?? null, toDate ?? null],
    queryFn: async () =>
      (
        await ecgService.getPatientEcgRecordings(patientId, fromDate, toDate)
      ).map(mapEcgSummary),
    enabled: enabled && patientId > 0,
  });

  // The endpoint returns 403 when the HCP can't access this patient — surface
  // that distinctly from "no recordings" (an empty 200).
  const isForbidden =
    axios.isAxiosError(error) && error.response?.status === 403;

  return {
    recordings: data ?? [],
    isError,
    isForbidden,
    isFetching,
    isInitialLoading,
    isRefetching,
    refetch,
  };
}

/**
 * Fetches and parses the waveform samples for a single recording. The API
 * proxies the artifact server-side, so this is a normal authenticated call;
 * it only runs while the tracing view is open.
 */
export function useEcgWaveform(summary: IEcgRecordingSummary | null) {
  const { data, isError, isFetching, isInitialLoading } =
    useQuery<IEcgRecording>({
      queryKey: ["ecg-waveform", summary?.id],
      enabled: !!summary?.id,
      queryFn: async () => {
        const raw = await ecgService.getEcgWaveform(summary!.id);

        return parseEcgData(raw, {
          id: summary!.id,
          classification: summary!.classification,
          amplitudeScale: summary!.amplitudeScale,
          amplitudeUnit: summary!.amplitudeUnit,
        });
      },
    });

  return {
    recording: data ?? null,
    isError,
    isLoading: isFetching || isInitialLoading,
  };
}
